/**
 * useResolvedCalendarSlot — resolve a calendar proposal's display time.
 *
 * Every production caller now sends authoritative `start_time`/`end_time` on
 * the payload (the slot-reference resolve path that used to backfill a null
 * payload from `/scheduling/slots/resolve/` was removed once callers stopped
 * passing a `slotRef` — see git history for the prior `useQuery`-based
 * version). This hook is now a pure formatter: it reports 'resolved' when
 * both stored times are present and 'unresolved' otherwise. It never coerces
 * a missing time into a date.
 */
export interface ResolvedCalendarTime {
  /** Resolved (or already-stored) start time in ISO 8601, or null while unresolved. */
  startTime: string | null;
  /** Resolved (or already-stored) end time in ISO 8601, or null while unresolved. */
  endTime: string | null;
  /**
   * 'resolved' once a real time is available for display, 'unresolved' when
   * there's nothing to show (the payload is missing a start or end time).
   * There is no 'loading' state — resolution is now synchronous.
   */
  status: 'resolved' | 'unresolved';
}

export function useResolvedCalendarSlot(
  storedStartTime: string | null,
  storedEndTime: string | null
): ResolvedCalendarTime {
  if (storedStartTime && storedEndTime) {
    return {
      startTime: storedStartTime,
      endTime: storedEndTime,
      status: 'resolved',
    };
  }
  return { startTime: null, endTime: null, status: 'unresolved' };
}
